import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { keyServices as fallbackServices } from '../../../data/services';
import { useScrollReveal } from '../../../hooks/useScrollReveal';
import { usePortal } from '../PortalContext';
import { cardAccent } from '../sectionPalette';
import { SectionHeading } from './SectionHeading';

/* Keyword → specific RemixIcon. Ordered most-specific first so e.g.
   "INDUSTRY BUILDING PERMISSION" resolves to the industry icon, not the
   generic building one. This guarantees each service gets a meaningful icon
   even when the API/url-library row carries a generic or empty icon. */
const ICON_RULES: [RegExp, string][] = [
  [/telecom|tower/i, 'ri-signal-tower-line'],
  [/demolition|demolish/i, 'ri-hammer-line'],
  [/industr/i, 'ri-building-4-line'],
  [/\bbps\b|penalis|penaliz/i, 'ri-scales-3-line'],
  [/\blrs\b|regularis|regulariz/i, 'ri-stack-line'],
  [/\bclu\b|land\s*use/i, 'ri-exchange-funds-line'],
  [/\btdr\b|development\s*rights/i, 'ri-arrow-left-right-line'],
  [/ucims|census/i, 'ri-database-2-line'],
  [/municipal|asset/i, 'ri-community-line'],
  [/layout|plot/i, 'ri-layout-grid-line'],
  [/building|construction|house/i, 'ri-building-line'],
  [/permission|permit|approval|licen[cs]e/i, 'ri-file-shield-2-line'],
  [/grievance|complaint/i, 'ri-feedback-line'],
  [/payment|fee|tax/i, 'ri-bank-card-line'],
  [/map|gis|plan/i, 'ri-map-2-line'],
];

const GENERIC_ICONS = new Set(['', 'ri-service-line', 'ri-link', 'ri-links-line', 'ri-global-line']);

/* Pick the icon for a card: honour the original icon supplied by the data/API
   first (this is the intended icon for the service); otherwise fall back to a
   keyword match on the title; otherwise a sensible default. */
const resolveServiceIcon = (title = '', provided = ''): string => {
  if (provided && !GENERIC_ICONS.has(provided)) return provided;
  for (const [re, icon] of ICON_RULES) if (re.test(title)) return icon;
  return 'ri-government-line';
};

/* Keyword → curated local photo (served from /public/images/services). Hand-
   picked, freely-licensed images so each card shows a relevant, on-topic photo
   with no external dependency. Ordered most-specific first (e.g. "INDUSTRY
   BUILDING PERMISSION" resolves to the industry photo, not the building one). */
const IMAGE_RULES: [RegExp, string][] = [
  [/telecom|tower/i, 'telecom-tower'],
  [/demolition|demolish/i, 'demolition'],
  [/industr/i, 'industry-building'],
  [/\bbps\b|penalis|penaliz/i, 'bps'],
  [/\blrs\b|regularis|regulariz/i, 'lrs'],
  [/\bclu\b|land\s*use/i, 'clu-permission'],
  [/\btdr\b|development\s*rights/i, 'tdr'],
  [/ucims|census/i, 'ucims'],
  [/municipal|asset/i, 'municipal-assets'],
  [/layout|plot/i, 'layout-permission'],
  [/building|construction|house|permission|permit|approval|licen[cs]e/i, 'building-permission'],
];

/* Resolve a photo for a card: an explicit image from the API wins; otherwise a
   curated local photo keyed by the title. If nothing matches (an unknown
   service), return '' so the card header gracefully shows the accent gradient. */
const resolveServiceImage = (title = '', provided = ''): string => {
  if (provided) return provided;
  for (const [re, slug] of IMAGE_RULES) if (re.test(title)) return `/images/services/${slug}.jpg`;
  return '';
};

export const KeyServicesSection: React.FC = () => {
  const { t } = useTranslation();
  const { ref, isRevealed } = useScrollReveal({ threshold: 0.15 });
  const { siteId, publicApi } = usePortal();
  const [keyServices, setKeyServices] = useState(fallbackServices);

  useEffect(() => {
    publicApi.get(`/url-library?siteId=${siteId}`)
      .then(res => {
        const data = res.data?.data || [];
        if (data.length > 0) {
          setKeyServices(data.map((u: any) => ({
            icon: u.icn_nm || 'ri-service-line',
            title: u.url_ttl || '',
            description: u.url_dscrptn || '',
            url: u.url_txt || '',
            image: u.img_url_tx || u.imge_url_tx || '',
            openInNewTab: u.opn_in_nw_tb === 1 || u.opn_in_nw_tb === true,
          })));
        } else {
          setKeyServices(fallbackServices);
        }
      })
      .catch(() => setKeyServices(fallbackServices));
  }, [siteId]);

  const renderCard = (svc: any, i: number) => {
    const href = svc.url || '#';
    const isExternal = /^https?:\/\//i.test(href);
    const target = svc.openInNewTab || isExternal ? '_blank' : undefined;
    const rel = target === '_blank' ? 'noopener noreferrer' : undefined;
    const [from, to] = cardAccent(i);
    const icon = resolveServiceIcon(svc.title, svc.icon);
    const image = resolveServiceImage(svc.title, svc.image);
    const cta = isExternal ? 'Apply Online' : 'Open Service';
    const gradient = `linear-gradient(135deg, ${from}, ${to})`;
    return (
      <a
        key={i}
        href={href}
        target={target}
        rel={rel}
        aria-disabled={!svc.url}
        onClick={(e) => { if (!svc.url) e.preventDefault(); }}
        className="stagger-child group relative flex flex-col h-full rounded-2xl overflow-hidden no-underline bg-white dark:bg-white/[0.04] border border-black/[0.07] dark:border-white/10 shadow-[0_4px_14px_-6px_rgba(0,0,0,0.25)] transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_22px_44px_-16px_rgba(0,0,0,0.4)]"
      >
        {/* Top hairline in the card's own tone — draws across on hover, which
            is what ties the photo header to the CTA colour below. */}
        <span
          aria-hidden="true"
          className="absolute inset-x-0 top-0 z-20 h-[3px] origin-left scale-x-0 transition-transform duration-500 group-hover:scale-x-100"
          style={{ backgroundImage: gradient }}
        />

        {/* Photo header — falls back to the accent gradient if the image can't load */}
        <div className="relative h-28 sm:h-32 w-full overflow-hidden" style={{ backgroundImage: gradient }}>
          {image && (
            <img
              src={image}
              alt={svc.title}
              loading="lazy"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[600ms] group-hover:scale-[1.07]"
            />
          )}
          {/* Readability scrim at the bottom of the photo */}
          <span className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent pointer-events-none" />
          {/* Icon badge */}
          <div
            className="absolute bottom-2.5 left-3 z-10 w-10 h-10 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center text-white shadow-[0_6px_16px_-4px_rgba(0,0,0,0.5)] ring-2 ring-white/35 transition-all duration-300 group-hover:scale-110 group-hover:-rotate-3"
            style={{ backgroundImage: gradient }}
          >
            <i className={`${icon} text-xl sm:text-[22px] drop-shadow-sm`} />
          </div>
        </div>

        {/* Body */}
        <div className="flex flex-col flex-1 p-4">
          <h3 className="text-[13.5px] sm:text-[15px] font-bold leading-snug tracking-[-0.01em] text-[#161A22] dark:text-white line-clamp-2">
            {svc.title}
          </h3>
          {svc.description && (
            <p className="mt-1.5 text-[12px] sm:text-[13px] text-[#6B7280] dark:text-white/55 leading-relaxed line-clamp-2 flex-1">
              {svc.description}
            </p>
          )}
          <div className="mt-3 pt-3 border-t border-black/[0.07] dark:border-white/10">
            <span
              className="flex items-center justify-between text-[11.5px] sm:text-[12.5px] font-extrabold uppercase tracking-[0.06em]"
              style={{ color: from }}
            >
              {cta}
              <i className="ri-arrow-right-line text-[15px] transition-transform duration-300 group-hover:translate-x-1" />
            </span>
          </div>
        </div>
      </a>
    );
  };

  return (
    <section
      id="services"
      ref={ref as React.RefObject<HTMLElement>}
      className={`scroll-reveal portal-section portal-surface portal-rule scroll-mt-16 ${isRevealed ? 'revealed' : ''}`}
    >
      <div className="reveal-content portal-container">
        <SectionHeading
          eyebrow="Online Services"
          title={t('keyServices.title')}
          subtitle="Apply, track and download — every departmental service available to citizens and officers, in one place."
          action={
            <a
              href="/services"
              className="group hidden md:inline-flex items-center gap-1.5 text-[13px] font-bold text-[color:var(--portal-accent-dark)] bg-[var(--portal-accent-soft)] px-5 py-2.5 rounded-full hover:bg-[var(--portal-accent)] hover:text-white transition-all duration-300 hover:-translate-y-0.5"
              style={{ boxShadow: 'inset 0 0 0 1px color-mix(in srgb, var(--portal-accent) 35%, transparent)' }}
            >
              {t('keyServices.viewAll')}
              <i className="ri-arrow-right-line text-sm" />
            </a>
          }
        />

        {/* Service Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3.5 sm:gap-5 items-stretch">
          {keyServices.map((svc, i) => renderCard(svc, i))}
        </div>
      </div>
    </section>
  );
};
